import { useAuth } from '@clerk/react'

import { useMemo } from 'react'
import { Link, useLocation } from '#router'
import { FeaturebaseBugButton } from '@/components/featurebase-bug-button'
import { MobileListsDrawer } from '@/components/mobile-lists-drawer'
import {
  ProblemListProvider,
  useProblemListsOptional,
} from '@/components/problem-lists/problem-list-context'
import { StarDropdown } from '@/components/problem-lists/star-dropdown'
import { ProblemsFilterPopover } from '@/components/problems/problems-filter-popover'
import { ProblemsSearchBar } from '@/components/problems/problems-search-bar'
import { ProblemsSortPopover } from '@/components/problems/problems-sort-popover'
import { TopicPills } from '@/components/problems/topic-pills'
import {
  applyFilters,
  createProblemsFuse,
  deriveAvailableDifficulties,
  deriveAvailableTopics,
  type SortOption,
  searchProblems,
  sortProblems,
} from '@/components/problems/types'
import { ProgressStatusIcon } from '@/components/ui/progress-status-icon'
import { useDevice } from '@/hooks/use-mobile'
import { useProblemsState } from '@/hooks/use-problems-state'
import { isSolvedStatus } from '@/lib/progress-status'
import { trpc } from '@/lib/trpc'
import type { ListRef } from '@/lib/url-state'
import { cn } from '@/lib/utils'
import { ShuffleButton } from './shuffle-button'

const PROBLEMS_SORT_OPTIONS = ['difficulty', 'questionId', 'status']

const PROBLEMS_SORT_POPOVER_OPTIONS = [
  { value: 'difficulty', label: 'Difficulty' },
  { value: 'questionId', label: 'Question ID' },
  { value: 'status', label: 'Status' },
]

export function MobileProblems() {
  return (
    <ProblemListProvider>
      <MobileProblemsContent />
    </ProblemListProvider>
  )
}

function MobileProblemsContent() {
  const { navigate } = useLocation()
  const listCtx = useProblemListsOptional()
  const {
    data: problems,
    isLoading,
    error,
    refetch,
  } = trpc.problems.browse.useQuery()
  const { isSignedIn } = useAuth()
  const { isTablet } = useDevice()

  const { data: userProgress } = trpc.problems.getUserProgress.useQuery(
    undefined,
    { enabled: !!isSignedIn },
  )

  // Scoped to this page's problems — the progress map also carries
  // propagated duplicate entries and non-BCTCI solves.
  const solvedCount = useMemo(() => {
    if (!userProgress?.progress || !problems) return 0
    return problems.filter((p) => isSolvedStatus(userProgress.progress[p.id]))
      .length
  }, [userProgress, problems])

  const userListRefs: ListRef[] = useMemo(
    () =>
      listCtx
        ? listCtx.lists.map((l) => ({
            id: l.id,
            name: l.name,
            isSystem: l.isSystem,
          }))
        : [],
    [listCtx],
  )

  const availableTopics = useMemo(
    () => (problems ? deriveAvailableTopics(problems) : []),
    [problems],
  )

  const availableDifficulties = useMemo(
    () => (problems ? deriveAvailableDifficulties(problems) : []),
    [problems],
  )

  const {
    filters,
    sortBy,
    sortDir,
    searchQuery,
    setSearchQuery,
    setSortBy,
    setSortDir,
    matchMode,
    setMatchMode,
    showTags,
    setShowTags,
    toggleFilterValue,
    resetFilters,
    setFilterOperator,
  } = useProblemsState(
    'bctci-browse-mobile',
    userListRefs,
    availableTopics,
    PROBLEMS_SORT_OPTIONS,
  )

  const fuse = useMemo(() => createProblemsFuse(problems ?? []), [problems])

  const filteredProblems = useMemo(() => {
    if (!problems) return []
    const searched = searchProblems(problems, fuse, searchQuery)
    return applyFilters(
      searched,
      filters,
      matchMode,
      userProgress?.progress,
      listCtx?.memberships,
    )
  }, [
    problems,
    searchQuery,
    fuse,
    filters,
    matchMode,
    userProgress,
    listCtx?.memberships,
  ])

  const sortedProblems = useMemo(
    () =>
      sortProblems(
        filteredProblems,
        sortBy as SortOption,
        sortDir,
        userProgress?.progress,
      ),
    [filteredProblems, sortBy, sortDir, userProgress],
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-muted-foreground text-sm">Loading problems...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16">
        <p className="text-sm text-red-500">Failed to load problems</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="text-sm text-blue-500 underline"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      {/* Topic pills */}
      <TopicPills problems={problems ?? []} />

      {/* Search and filter controls */}
      {isTablet ? (
        <div className="flex items-center gap-1">
          <MobileListsDrawer />
          <ProblemsSearchBar value={searchQuery} onChange={setSearchQuery} />
          <ProblemsSortPopover
            sortBy={sortBy}
            sortDir={sortDir}
            onSortChange={(newSort, newDir) => {
              setSortBy?.(newSort)
              setSortDir?.(newDir)
            }}
            showTags={showTags}
            onToggleTags={() => setShowTags((prev) => !prev)}
            sortOptions={PROBLEMS_SORT_POPOVER_OPTIONS}
          />
          <ProblemsFilterPopover
            filters={filters}
            matchMode={matchMode}
            onMatchModeChange={setMatchMode}
            onToggleFilter={toggleFilterValue}
            onOperatorChange={setFilterOperator}
            availableDifficulties={availableDifficulties}
            availableTopics={availableTopics}
            availableLists={userListRefs}
            onReset={resetFilters}
          />
          <div className="flex-1" />
          {isSignedIn && <FeaturebaseBugButton />}
          <span className="text-muted-foreground text-xs">
            {solvedCount}/{problems?.length ?? 0} Solved
          </span>
          <ShuffleButton
            problems={sortedProblems}
            ariaLabel="Solve a random problem"
          />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <ProblemsSearchBar value={searchQuery} onChange={setSearchQuery} />
          <div className="flex items-center gap-2">
            <MobileListsDrawer />
            <ProblemsSortPopover
              sortBy={sortBy}
              sortDir={sortDir}
              onSortChange={(newSort, newDir) => {
                setSortBy?.(newSort)
                setSortDir?.(newDir)
              }}
              showTags={showTags}
              onToggleTags={() => setShowTags((prev) => !prev)}
              sortOptions={PROBLEMS_SORT_POPOVER_OPTIONS}
            />
            <ProblemsFilterPopover
              filters={filters}
              matchMode={matchMode}
              onMatchModeChange={setMatchMode}
              onToggleFilter={toggleFilterValue}
              onOperatorChange={setFilterOperator}
              availableDifficulties={availableDifficulties}
              availableTopics={availableTopics}
              availableLists={userListRefs}
              onReset={resetFilters}
            />
            {isSignedIn && <FeaturebaseBugButton />}
            <span className="text-muted-foreground ml-auto text-xs">
              {solvedCount}/{problems?.length ?? 0} Solved
            </span>
            <ShuffleButton
              problems={sortedProblems}
              ariaLabel="Solve a random problem"
            />
          </div>
        </div>
      )}

      {/* Problem list */}
      <div className="flex flex-col">
        {sortedProblems.map((problem) => {
          const progress = userProgress?.progress?.[problem.id]
          const via = userProgress?.solvedVia?.[problem.id]
          const rowContent = (
            <>
              {/* Status indicator */}
              <div className="flex w-6 shrink-0 items-center justify-center">
                <ProgressStatusIcon status={progress} viaTitle={via?.title} />
              </div>

              {/* Problem info */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-xs">
                    {problem.externalId}
                  </span>
                  <span className="truncate text-sm font-medium">
                    {problem.title}
                  </span>
                </div>
                {showTags && problem.topics.length > 0 && (
                  <div className="mt-0.5 flex flex-wrap gap-1">
                    {problem.topics.slice(0, 3).map((topic) => (
                      <button
                        key={topic.slug}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/problems/topics/${topic.slug}`)
                        }}
                        className="bg-muted text-muted-foreground hover:bg-blue-100 hover:text-blue-600 dark:hover:bg-blue-900/30 dark:hover:text-blue-400 rounded px-1.5 py-0.5 text-[10px] transition-colors"
                      >
                        {topic.name}
                      </button>
                    ))}
                    {problem.topics.length > 3 && (
                      <span className="text-muted-foreground text-[10px]">
                        +{problem.topics.length - 3}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Difficulty */}
              <span
                className={cn(
                  'shrink-0 text-xs font-medium',
                  problem.difficulty?.name === 'Easy' &&
                    'text-green-600 dark:text-green-400',
                  problem.difficulty?.name === 'Medium' &&
                    'text-yellow-600 dark:text-yellow-400',
                  problem.difficulty?.name === 'Hard' &&
                    'text-red-600 dark:text-red-400',
                )}
              >
                {problem.difficulty?.name}
              </span>
            </>
          )

          // Tablet: use div with programmatic nav so StarDropdown popover
          // can be dismissed by tapping outside without triggering navigation
          if (isTablet) {
            return (
              <div
                key={problem.id}
                role="link"
                tabIndex={0}
                onClick={() => navigate(`/editor?problem=${problem.slug}`)}
                className="flex cursor-pointer items-center gap-3 border-b px-1 py-3 transition-colors hover:bg-accent active:bg-accent"
              >
                {rowContent}
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="ml-1 mr-1 flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center"
                >
                  <StarDropdown problemId={problem.id} />
                </div>
              </div>
            )
          }

          return (
            <Link
              key={problem.id}
              href={`/editor?problem=${problem.slug}`}
              className="flex items-center gap-3 border-b px-1 py-3 transition-colors hover:bg-accent"
            >
              {rowContent}
            </Link>
          )
        })}
      </div>
    </div>
  )
}
